import type { AvailableUnit as CatalogueUnit, CatalogueDesign } from '../api/masters';
import {
  claimedOut,
  garmentWear,
  lookLabel,
  lookOptions,
  optionAvailability,
  pickGarment,
  sizeKey,
  sizeLabel,
  sizeOptions,
} from './catalogue';
import { isFree, type AvailableUnit } from './rail';

/**
 * The booking's looks step as the owner chose it (2026-10-17, "A+B"): who wears it is picked first,
 * then each design card carries its sizes as buttons — one tap adds that size for that person — and
 * "Each garment" lists the rail grouped by design, every row saying whose bag it is in.
 *
 * <p>
 * <strong>A size is a button only where a size says everything.</strong> A design that comes in two
 * colours has two 38s, and a tap cannot know which was meant, so its card asks for the colour
 * (`needsLook`) and the design's own page takes it from there.
 * </p>
 */

/** One size button on a design card. */
export interface CardSize {
  key: string;
  /** "38", "40 · Slim", or "One size" for a design with no size axis. */
  label: string;
  /** In the selected person's bag, free to add, or none free. */
  state: 'mine' | 'free' | 'out';
  /** Under the size: the person it is for, "2 free", or "back 20-10-2026". */
  line: string;
  /** What a tap adds — the garment booking by size sets aside (`pickGarment`). */
  adds: CatalogueUnit | null;
  /** What a tap takes out — the selected person's garment of this size. */
  removes: number | null;
}

export function cardSizes(
  design: Pick<CatalogueDesign, 'units'>,
  days: number,
  claimed: ReadonlySet<number>,
  mine: ReadonlySet<number>,
  forName: string,
): { needsLook: boolean; sizes: CardSize[] } {
  // Garments already in this booking are not free to add again, whoever they are for.
  const units = claimedOut(design.units, claimed);

  if (lookOptions(units, days).length > 1) {
    return { needsLook: true, sizes: [] };
  }

  const sizes = sizeOptions(units, undefined, days).map((option): CardSize => {
    const label = option.label || 'One size';
    const theirs = design.units.find(
      (unit) => sizeKey(unit) === option.key && mine.has(unit.unitId),
    );

    if (theirs) {
      return {
        key: option.key,
        label,
        state: 'mine',
        line: forName,
        adds: null,
        removes: theirs.unitId,
      };
    }

    return option.freeCount > 0
      ? {
          key: option.key,
          label,
          state: 'free',
          line: optionAvailability(option),
          adds: pickGarment(units, { size: option.key }),
          removes: null,
        }
      : {
          key: option.key,
          label,
          state: 'out',
          line: optionAvailability(option),
          adds: null,
          removes: null,
        };
  });

  return { needsLook: false, sizes };
}

/** One design's garments in the "Each garment" list. */
export interface RailGroup<T> {
  itemId: number;
  name: string;
  free: number;
  total: number;
  units: T[];
}

/**
 * The rail grouped by design, in name order; free garments first within each, then by code. With
 * `freeOnly`, a garment out with somebody else is left off — but one already in this booking stays,
 * since that is where the clerk takes it back out.
 */
export function railGroups<T extends AvailableUnit>(
  units: readonly T[],
  { freeOnly, claimed }: { freeOnly: boolean; claimed: ReadonlySet<number> },
): RailGroup<T>[] {
  const groups = new Map<number, RailGroup<T>>();

  for (const unit of units) {
    const group = groups.get(unit.itemId) ?? {
      itemId: unit.itemId,
      name: unit.itemName,
      free: 0,
      total: 0,
      units: [],
    };

    group.total += 1;
    group.free += isFree(unit) && !claimed.has(unit.unitId) ? 1 : 0;

    if (!freeOnly || isFree(unit) || claimed.has(unit.unitId)) {
      group.units.push(unit);
    }

    groups.set(unit.itemId, group);
  }

  return [...groups.values()]
    .filter((group) => group.units.length > 0)
    .map((group) => ({
      ...group,
      units: group.units.sort(
        (a, b) =>
          Number(isFree(b)) - Number(isFree(a)) ||
          a.serialCode.localeCompare(b.serialCode, 'en', { numeric: true }),
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** What a garment's photo row says under its code (owner, 2026-10-17, gallery option 2). */
export interface GarmentCaption {
  /** "Size 42 · Maroon" — or the variant's own name when the catalogue has not answered. */
  look: string;
  /** "Excellent · rented 3 times", or null without the catalogue's garment to read it from. */
  wear: string | null;
}

/**
 * The caption for one garment on the rail, read from its catalogue twin when there is one — the
 * rail knows the dates, the catalogue knows the garment's axes, condition and rentals.
 */
export function garmentCaption(
  unit: Pick<AvailableUnit, 'variantDisplayName'>,
  twin: Pick<CatalogueUnit, 'axes' | 'condition' | 'rentalCount'> | undefined,
): GarmentCaption {
  if (!twin) {
    return { look: unit.variantDisplayName, wear: null };
  }

  const size = sizeLabel(twin);
  const look = [size ? `Size ${size}` : '', lookLabel(twin)].filter(Boolean).join(' · ');

  return { look: look || unit.variantDisplayName, wear: garmentWear(twin) };
}
